/* =============================================================================
   Upload Center × Eurus — motion layer (ucx)
   dtfintexas.com · Eurus theme

   Progressive enhancement for the Upload Center field. It never talks to the
   app or to FilePond's API: it only reads the rendered DOM and listens to the
   FilePond:* DOM events the field already dispatches. If this file or
   uc-motion.css fails to load, the stock field keeps working unchanged.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__ucxBooted) return;
  window.__ucxBooted = true;

  var CFG = Object.assign({
    cssUrl: '',
    badge: 'Upload your design',
    cta: 'Browse files',
    ctaTouch: 'Tap to upload',
    subTouch: 'Pick a file from your device',
    armed: 'Drop your files in this box',
    over: 'Release to upload',
    celebrate: true,
    nudgeAddToCart: true,
    thumbnails: true
  }, window.UCX_CONFIG || {});

  var WRAP = '.upload-center-field-wrapper';
  var ROOT = '.filepond--root';
  var NS = 'http://www.w3.org/2000/svg';
  var SPECTRUM = ['--ucx-s1', '--ucx-s2', '--ucx-s3', '--ucx-s4', '--ucx-s5', '--ucx-s6'];
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var html = document.documentElement;
  var cssReady = null;

  // tray + arrow; the arrow group is animated
  var ICON_UPLOAD =
    '<svg class="ucx-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/>' +
    '<g class="ucx-arrow"><path d="M12 15V4"/><path d="m7.5 8.5 4.5-4.5 4.5 4.5"/></g></svg>';
  var ICON_DOWN =
    '<svg class="ucx-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M12 5v14"/><path d="m6 13 6 6 6-6"/></svg>';
  var ICON_CTA =
    '<svg class="ucx-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M12 16V4"/><path d="m7 9 5-5 5 5"/><path d="M5 20h14"/></svg>';

  function h(tag, cls, markup) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (markup) n.innerHTML = markup;
    return n;
  }

  function text(node, value) {
    node.textContent = value;
    return node;
  }

  function loadCss() {
    if (cssReady) return cssReady;
    cssReady = new Promise(function (resolve) {
      if (!CFG.cssUrl || document.querySelector('link[data-ucx]')) return resolve(true);
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CFG.cssUrl;
      link.setAttribute('data-ucx', '');
      link.onload = function () { resolve(true); };
      link.onerror = function () { resolve(false); };
      document.head.appendChild(link);
    });
    return cssReady;
  }

  /* -------------------------------------------------------------------------
     Read the app-embed style settings off the rendered field
     ---------------------------------------------------------------------- */
  function readTokens(st) {
    var s = st.wrap.style;
    var drip = st.root.querySelector('.filepond--drip');
    var label = st.root.querySelector('.filepond--drop-label');
    var panel = st.root.querySelector('.filepond--panel.filepond--panel-root');

    // Panel BG: painted once on the whole field (see the panel note in the CSS)
    if (panel) {
      var p = getComputedStyle(panel);
      s.setProperty('--ucx-paper', p.backgroundImage !== 'none' ? p.backgroundImage : p.backgroundColor);
    }

    if (drip) {
      var d = getComputedStyle(drip);
      if (d.borderTopStyle !== 'none' && parseFloat(d.borderTopWidth) > 0) {
        s.setProperty('--ucx-line', d.borderTopColor);
        s.setProperty('--ucx-bw', Math.max(parseFloat(d.borderTopWidth), 1) + 'px');
      }
      st.radius = parseFloat(d.borderTopLeftRadius) || 0;
      s.setProperty('--ucx-r', st.radius + 'px');
    }
    if (label) s.setProperty('--ucx-ink', getComputedStyle(label).color);
  }

  /* -------------------------------------------------------------------------
     Label: ink tile, headline, spec chips, CTA
     Only the app's default label is re-laid out; a Custom Label Html set in
     the app embed is left exactly as written.
     ---------------------------------------------------------------------- */
  function enhanceLabel(st) {
    var lab = st.root.querySelector('.ucCustomLabel');
    if (!lab || lab.__ucx) return;
    lab.__ucx = true;
    lab.classList.add('ucx-label');

    var title = lab.querySelector(':scope > b');
    var spans = lab.querySelectorAll(':scope > span');
    if (!title || !spans.length || !lab.querySelector(':scope > svg')) {
      lab.classList.add('ucx-label--custom');
      return;
    }
    lab.classList.add('ucx-label--default');

    var i = 0;
    var info = spans[spans.length - 1];
    var drag = spans.length > 1 ? spans[0] : null;

    var icon = h('span', 'ucx-icon ucx-anim',
      '<span class="ucx-icon__aura"></span><span class="ucx-icon__tile">' + ICON_UPLOAD + '</span>');
    icon.setAttribute('aria-hidden', 'true');
    lab.insertBefore(icon, lab.firstChild);
    stagger(icon, i++);

    title.classList.add('ucx-title', 'ucx-anim');
    stagger(title, i++);

    if (drag) {
      drag.classList.add('ucx-sub', 'ucx-sub--drag', 'ucx-anim');
      stagger(drag, i);
      if (CFG.subTouch) {
        var touch = text(h('span', 'ucx-sub ucx-sub--touch ucx-anim'), CFG.subTouch);
        drag.parentNode.insertBefore(touch, drag.nextSibling);
        stagger(touch, i);
      }
      i++;
    }

    var chips = buildChips(info);
    if (chips) {
      info.classList.add('ucx-sr');
      lab.appendChild(chips);
      stagger(chips, i++);
    } else {
      info.classList.add('ucx-sub', 'ucx-anim');
      stagger(info, i++);
    }

    if (CFG.cta) {
      var cta = h('span', 'ucx-cta ucx-anim', ICON_CTA);
      cta.appendChild(text(h('span', 'ucx-cta__desk'), CFG.cta));
      cta.appendChild(text(h('span', 'ucx-cta__touch'), CFG.ctaTouch || CFG.cta));
      lab.appendChild(cta);
      stagger(cta, i++);
    }
  }

  function stagger(node, i) {
    node.style.setProperty('--ucx-i', i);
  }

  // "PNG, JPEG, SVG Only <br> Max file size: 300MB" -> [PNG] [JPEG] [SVG] [Max 300MB]
  function buildChips(info) {
    var lines = [''];
    info.childNodes.forEach(function (n) {
      if (n.nodeName === 'BR') lines.push('');
      else lines[lines.length - 1] += n.textContent;
    });
    lines = lines.map(function (l) { return l.replace(/\s+/g, ' ').trim(); }).filter(Boolean);
    if (!lines.length) return null;

    var wrap = h('span', 'ucx-chips ucx-anim');
    wrap.setAttribute('aria-hidden', 'true');
    var j = 0;
    function chip(label, kind) {
      var c = text(h('span', 'ucx-chip ucx-chip--' + kind), label);
      c.style.setProperty('--ucx-j', j++);
      wrap.appendChild(c);
    }

    lines.forEach(function (line) {
      var size = line.match(/max(?:imum)?\s*file\s*size\s*:?\s*(.+)$/i);
      if (/\bonly$/i.test(line)) {
        var more = false;
        line.replace(/\s*only$/i, '').split(',').forEach(function (t) {
          t = t.trim();
          if (/(\.\.\.|…)$/.test(t)) { more = true; t = t.replace(/\s*(\.\.\.|…)$/, ''); }
          if (t) chip(t, 'type');
        });
        if (more) chip('+ more', 'note');
      } else if (size) {
        chip('Max ' + size[1].trim(), 'size');
      } else {
        chip(line, 'note');
      }
    });
    return wrap;
  }

  /* -------------------------------------------------------------------------
     Field
     ---------------------------------------------------------------------- */
  function enhance(wrap) {
    var root = wrap.querySelector(ROOT);
    if (!root) return;

    var st = { wrap: wrap, root: root, radius: 14 };
    wrap.__ucx = st;
    readTokens(st);

    if (CFG.badge) {
      st.badge = h('div', 'ucx-badge', '<span class="ucx-badge__chip"></span>');
      st.badge.setAttribute('aria-hidden', 'true');
      st.badge.appendChild(text(h('span', 'ucx-badge__text'), CFG.badge));
      st.badge.insertAdjacentHTML('beforeend', ICON_DOWN);
      wrap.appendChild(st.badge);
      wrap.classList.add('ucx--badge');
    }

    st.fx = h('div', 'ucx-fx', '<span class="ucx-grid"></span><span class="ucx-grid-hot"></span>');
    st.hot = st.fx.lastChild;

    st.ants = document.createElementNS(NS, 'svg');
    st.ants.setAttribute('class', 'ucx-ants');
    st.rect = document.createElementNS(NS, 'rect');
    st.rect.setAttribute('class', 'ucx-ants__rect');
    st.ants.appendChild(st.rect);

    st.frame = h('div', 'ucx-frame',
      '<span class="ucx-corner ucx-corner--tl"></span><span class="ucx-corner ucx-corner--tr"></span>' +
      '<span class="ucx-corner ucx-corner--bl"></span><span class="ucx-corner ucx-corner--br"></span>');

    st.drop = h('div', 'ucx-drop',
      '<div class="ucx-drop__card"><span class="ucx-drop__icon"><span class="ucx-drop__aura"></span>' +
      '<span class="ucx-drop__tile">' + ICON_UPLOAD + '</span></span></div>');
    var card = st.drop.firstChild;
    card.appendChild(text(h('span', 'ucx-drop__armed'), CFG.armed));
    card.appendChild(text(h('span', 'ucx-drop__over'), CFG.over));

    [st.fx, st.ants, st.frame, st.drop].forEach(function (n) {
      n.setAttribute('aria-hidden', 'true');
      root.appendChild(n);
    });

    enhanceLabel(st);
    syncSize(st);
    syncFiles(st);
    bind(st);
    wrap.classList.add('ucx');
  }

  function teardown(st) {
    if (st.ro) st.ro.disconnect();
    if (io) io.unobserve(st.root);
    if (st.badge) st.badge.remove();
    st.wrap.classList.remove('ucx', 'ucx--badge', 'ucx-in', 'ucx-visible', 'ucx-has-files');
    st.wrap.__ucx = null;
  }

  function syncSize(st) {
    var w = st.root.offsetWidth;
    var hgt = st.root.offsetHeight;
    if (!w || !hgt) return;
    var inset = parseFloat(getComputedStyle(st.wrap).getPropertyValue('--ucx-bw')) / 2 || 0.75;
    st.ants.setAttribute('width', w);
    st.ants.setAttribute('height', hgt);
    st.rect.setAttribute('x', inset);
    st.rect.setAttribute('y', inset);
    st.rect.setAttribute('width', Math.max(w - inset * 2, 0));
    st.rect.setAttribute('height', Math.max(hgt - inset * 2, 0));
    st.rect.setAttribute('rx', Math.max(st.radius - inset, 0));
    st.wrap.style.setProperty('--ucx-h', hgt + 'px');
  }

  function syncFiles(st) {
    st.wrap.classList.toggle('ucx-has-files', !!st.root.querySelector('.filepond--item'));
    decorateItems(st);
  }

  // file-type tag on each row's meta line + the thumbnail card at its head
  function decorateItems(st) {
    st.root.querySelectorAll('.filepond--item').forEach(function (item) {
      var sub = item.querySelector('.filepond--file-info-sub');
      var main = item.querySelector('.filepond--file-info-main');
      if (!sub || !main) return;
      var ext = extOf(main.textContent);
      if (ext && !sub.hasAttribute('data-ucx-ext')) sub.setAttribute('data-ucx-ext', ext);
      if (CFG.thumbnails) paintThumb(item, ext);
    });
  }

  function extOf(name) {
    var m = String(name || '').trim().match(/\.([a-z0-9]{2,5})$/i);
    return m ? m[1].toUpperCase() : '';
  }

  // drives the progress line on the row's bottom edge
  function setProgress(file, value) {
    var item = file && file.id && document.getElementById('filepond--item-' + file.id);
    var wrapper = item && item.querySelector('.filepond--file-wrapper');
    if (wrapper) wrapper.style.setProperty('--ucx-p', Math.max(0, Math.min(1, value)).toFixed(3));
  }

  function bind(st) {
    var root = st.root;

    if ('ResizeObserver' in window) {
      st.ro = new ResizeObserver(function () { syncSize(st); });
      st.ro.observe(root);
    }
    if (io) io.observe(root);
    else st.wrap.classList.add('ucx-in', 'ucx-visible');

    // the darker dot grid follows the cursor
    var raf = 0, mx = 0, my = 0;
    root.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' || !finePointer.matches) return;
      var r = root.getBoundingClientRect();
      mx = e.clientX - r.left;
      my = e.clientY - r.top;
      if (!raf) raf = requestAnimationFrame(function () {
        raf = 0;
        st.hot.style.setProperty('--ucx-mx', mx + 'px');
        st.hot.style.setProperty('--ucx-my', my + 'px');
      });
    });

    root.addEventListener('FilePond:addfile', function (e) {
      if (e.detail && e.detail.error) shake(st);
      else {
        replay(root, 'ucx-bump', 520);
        requestThumb(e.detail && e.detail.file);
      }
      later(st);
    });
    root.addEventListener('FilePond:removefile', function (e) {
      if (e.detail && e.detail.file) dropThumb(e.detail.file.id);
      later(st);
    });
    root.addEventListener('FilePond:warning', function () { shake(st); });
    root.addEventListener('FilePond:processfilestart', function (e) {
      setProgress(e.detail && e.detail.file, 0);
    });
    root.addEventListener('FilePond:processfileprogress', function (e) {
      if (e.detail) setProgress(e.detail.file, e.detail.progress || 0);
    });
    root.addEventListener('FilePond:processfile', function (e) {
      if (e.detail && e.detail.error) return shake(st);
      setProgress(e.detail && e.detail.file, 1);
      celebrateSoon(st, e.detail && e.detail.file);
    });
  }

  function later(st) {
    setTimeout(function () { syncFiles(st); }, 60);
    setTimeout(function () { syncFiles(st); }, 700);
  }

  function replay(node, cls, ms) {
    node.classList.remove(cls);
    void node.offsetWidth;
    node.classList.add(cls);
    clearTimeout(node['__' + cls]);
    node['__' + cls] = setTimeout(function () { node.classList.remove(cls); }, ms);
  }

  function shake(st) {
    replay(st.root, 'ucx-shake', 500);
  }

  /* -------------------------------------------------------------------------
     Thumbnails: client-side only, built so the page never stutters.
     The card shows the top-centre square of the art (cover), so only that
     square is ever needed.
     1. Dimensions come from the file header (first 256 KB), never from a
        decode, so every decision is made before any pixel work.
     2. Normal images: decode + downscale in a Web Worker (createImageBitmap
        with a resize hint + OffscreenCanvas) -> ~5-10 KB WebP.
     3. PNGs too big to decode whole (long gang sheets): a streaming decoder
        in the same worker inflates IDAT with DecompressionStream, unfilters
        only the first `width` rows, box-filters them straight into the
        thumbnail and stops reading. Memory: two scanlines. Height: any.
     4. One file at a time; a job past its time budget is killed together
        with its worker and the row keeps its type placeholder. An idle
        worker is released after 15 s.
     5. SVG is vector: shown as-is through <img>, size-capped.
     6. No worker / OffscreenCanvas: async <img> decode on the main thread
        under stricter limits.
     ---------------------------------------------------------------------- */
  var lowEnd = (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
    window.matchMedia('(pointer: coarse)').matches;
  var TH = Object.assign({
    side: 144,                                // device px: 44px card at up to 3x
    maxPixels: lowEnd ? 12e6 : 40e6,          // full decode (≈ 48 / 160 MB RGBA)
    maxBytes: lowEnd ? 30e6 : 80e6,
    unknownMaxBytes: 15e6,                    // header unreadable
    streamMaxWidth: 16000,                    // streaming PNG path
    streamMaxWork: lowEnd ? 60e6 : 260e6,     // width × rows unfiltered (6600 wide = 43.6e6)
    mainMaxPixels: lowEnd ? 4e6 : 16e6,       // main-thread fallback
    svgMaxBytes: 2e6,
    timeout: lowEnd ? 4000 : 6000,
    streamTimeout: lowEnd ? 12000 : 8000
  }, CFG.thumbLimits || {});

  var thumbs = {};          // FilePond item id -> { state, ext, url }
  var jobs = [];
  var running = false;
  var worker = null;
  var workerUrl = null;
  var pending = {};
  var idleTimer = 0;
  var workerOff = !('Worker' in window) || typeof OffscreenCanvas === 'undefined';
  var canStream = typeof DecompressionStream !== 'undefined';

  function requestThumb(item) {
    if (!CFG.thumbnails || !item || !item.id) return;
    var id = item.id;
    var file = item.file;
    var ext = extOf(item.filename || (file && file.name));
    thumbs[id] = { state: 'pending', ext: ext };
    paintById(id);

    if (!(file instanceof Blob) || !file.size) return settle(id, null);
    if (/svg/i.test(file.type) || ext === 'SVG') {
      if (file.size > TH.svgMaxBytes) return settle(id, null);
      return readHead(file).then(function (buf) {
        var text = new TextDecoder().decode(new Uint8Array(buf, 0, Math.min(buf.byteLength, 65536)));
        settle(id, { url: URL.createObjectURL(file), ratio: svgRatio(text) });
      }, function () { settle(id, null); });
    }
    if (!/^image\//.test(file.type) && !/^(PNG|JPE?G|GIF|WEBP|AVIF|BMP)$/.test(ext)) return settle(id, null);

    readHead(file).then(function (buf) {
      if (!thumbs[id]) return;
      var dim = sniffSize(buf);
      var px = dim ? dim.w * dim.h : 0;
      var job = { id: id, file: file, w: dim ? dim.w : 0, h: dim ? dim.h : 0, mode: 'bitmap' };

      if (dim && dim.png && px > TH.maxPixels) {
        // too big to decode whole: stream the top square instead
        var rows = Math.min(dim.w, dim.h);
        if (dim.interlace || !canStream || workerOff || dim.w > TH.streamMaxWidth || dim.w * rows > TH.streamMaxWork) {
          return settle(id, null);
        }
        job.mode = 'stream';
      } else {
        if (file.size > TH.maxBytes || px > TH.maxPixels) return settle(id, null);
        if (!dim && file.size > TH.unknownMaxBytes) return settle(id, null);
      }
      jobs.push(job);
      pump();
    }, function () { settle(id, null); });
  }

  function dropThumb(id) {
    var t = thumbs[id];
    if (t && t.url) URL.revokeObjectURL(t.url);
    delete thumbs[id];
    jobs = jobs.filter(function (j) { return j.id !== id; });
  }

  function readHead(file) {
    var head = file.slice(0, 262144);
    return head.arrayBuffer ? head.arrayBuffer() : new Response(head).arrayBuffer();
  }

  // an SVG with only a viewBox has no intrinsic size, so object-fit cannot
  // cover with it: read the aspect ratio and size the <img> box instead
  function svgRatio(text) {
    var tag = (text.match(/<svg\b[^>]*>/i) || [''])[0];
    var vb = tag.match(/viewBox\s*=\s*["']\s*[-\d.e]+[\s,]+[-\d.e]+[\s,]+([\d.e]+)[\s,]+([\d.e]+)/i);
    if (vb && +vb[1] && +vb[2]) return +vb[1] / +vb[2];
    var w = tag.match(/\swidth\s*=\s*["']\s*([\d.]+)/i);
    var h = tag.match(/\sheight\s*=\s*["']\s*([\d.]+)/i);
    return w && h && +w[1] && +h[1] ? +w[1] / +h[1] : 1;
  }

  // width/height from PNG, GIF, WebP and JPEG headers without decoding
  function sniffSize(buf) {
    var b = new Uint8Array(buf);
    var dv = new DataView(buf);
    var n = b.length;
    if (n > 28 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) {
      return { w: dv.getUint32(16), h: dv.getUint32(20), png: true, interlace: b[28] };
    }
    if (n > 10 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) {
      return { w: dv.getUint16(6, true), h: dv.getUint16(8, true) };
    }
    if (n > 30 && b[0] === 0x52 && b[1] === 0x49 && b[8] === 0x57 && b[9] === 0x45) {
      var chunk = String.fromCharCode(b[12], b[13], b[14], b[15]);
      if (chunk === 'VP8X') return { w: 1 + (b[24] | b[25] << 8 | b[26] << 16), h: 1 + (b[27] | b[28] << 8 | b[29] << 16) };
      if (chunk === 'VP8 ') return { w: dv.getUint16(26, true) & 0x3fff, h: dv.getUint16(28, true) & 0x3fff };
      if (chunk === 'VP8L') {
        var bits = dv.getUint32(21, true);
        return { w: (bits & 0x3fff) + 1, h: ((bits >> 14) & 0x3fff) + 1 };
      }
    }
    if (n > 4 && b[0] === 0xFF && b[1] === 0xD8) {
      var i = 2;
      while (i + 9 < n) {
        if (b[i] !== 0xFF) { i++; continue; }
        var m = b[i + 1];
        if (m === 0xFF) { i++; continue; }
        if (m === 0x01 || (m >= 0xD0 && m <= 0xD9)) { i += 2; continue; }
        if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) {
          return { w: dv.getUint16(i + 7), h: dv.getUint16(i + 5) };
        }
        i += 2 + dv.getUint16(i + 2);
      }
    }
    return null;
  }

  function pump() {
    clearTimeout(idleTimer);
    if (!running && !jobs.length && worker) {
      idleTimer = setTimeout(function () { killWorker('idle'); }, 15000);   // free it once the queue is empty
    }
    if (running || !jobs.length) return;
    var job = jobs.shift();
    if (!thumbs[job.id]) return pump();
    running = true;
    var done = false;
    var timer = setTimeout(function () {
      finish(null);
      killWorker('timeout');                  // frees the decode in flight
    }, job.mode === 'stream' ? TH.streamTimeout : TH.timeout);

    function finish(res) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      running = false;
      settle(job.id, res);
      pump();
    }

    viaWorker(job).then(finish, function () { finish(null); });
  }

  function settle(id, res) {
    var t = thumbs[id];
    if (!t) {
      if (res && res.url) URL.revokeObjectURL(res.url);
      return;
    }
    if (res && (res.blob || res.url)) {
      t.url = res.url || URL.createObjectURL(res.blob);
      t.ratio = res.ratio || 1;
      t.state = 'ready';
    } else {
      t.state = 'skip';
    }
    paintById(id);
  }

  function viaWorker(job) {
    var w = getWorker();
    if (!w) return job.mode === 'stream' ? Promise.reject('no-worker') : mainThumb(job);
    return new Promise(function (resolve, reject) {
      pending[job.id] = { resolve: resolve, reject: reject };
      w.postMessage({ id: job.id, file: job.file, w: job.w, h: job.h, side: TH.side, mode: job.mode });
    }).catch(function (err) {
      if (err !== 'unsupported' || job.mode === 'stream') throw err;
      workerOff = true;
      return mainThumb(job);
    });
  }

  function getWorker() {
    if (workerOff) return null;
    if (worker) return worker;
    try {
      workerUrl = URL.createObjectURL(new Blob(['(' + thumbWorker.toString() + ')()'], { type: 'text/javascript' }));
      worker = new Worker(workerUrl);
      worker.onmessage = function (e) {
        var p = pending[e.data.id];
        if (!p) return;
        delete pending[e.data.id];
        if (e.data.error) p.reject(e.data.error);
        else p.resolve(e.data);
      };
      worker.onerror = function () {          // e.g. blocked by a CSP: fall back
        workerOff = true;
        killWorker('unsupported');
      };
    } catch (err) {
      workerOff = true;
      worker = null;
    }
    return worker;
  }

  function killWorker(reason) {
    if (worker) worker.terminate();
    if (workerUrl) URL.revokeObjectURL(workerUrl);
    worker = null;
    workerUrl = null;
    var open = pending;
    pending = {};
    Object.keys(open).forEach(function (k) { open[k].reject(reason); });
  }

  // top-centre square of a w × h image, scaled to at most `side`
  function topSquare(w, h, side) {
    var q = Math.min(w, h);
    return { q: q, t: Math.max(1, Math.min(side, q)), sx: Math.floor((w - q) / 2) };
  }

  // runs inside the worker: keep it self-contained
  function thumbWorker() {
    function topSquare(w, h, side) {
      var q = Math.min(w, h);
      return { q: q, t: Math.max(1, Math.min(side, q)), sx: Math.floor((w - q) / 2) };
    }

    function encode(canvas) {
      return canvas.convertToBlob({ type: 'image/webp', quality: 0.82 });
    }

    async function viaBitmap(d) {
      var hint = { imageOrientation: 'from-image', resizeQuality: 'medium' };
      if (d.w && d.h && Math.min(d.w, d.h) > d.side) {
        if (d.w <= d.h) hint.resizeWidth = d.side;   // short side lands on the card size
        else hint.resizeHeight = d.side;
      }
      var bmp;
      try { bmp = await createImageBitmap(d.file, hint); } catch (err) { bmp = await createImageBitmap(d.file); }
      try {
        var sq = topSquare(bmp.width, bmp.height, d.side);
        var canvas = new OffscreenCanvas(sq.t, sq.t);
        var ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, sq.sx, 0, sq.q, sq.q, 0, 0, sq.t, sq.t);
        return { blob: await encode(canvas), w: sq.t, h: sq.t };
      } finally {
        bmp.close();
      }
    }

    // streaming PNG: decode only the rows the thumbnail needs
    async function viaPngStream(d) {
      var file = d.file;
      async function bytes(start, len) {
        return new Uint8Array(await file.slice(start, start + len).arrayBuffer());
      }
      function u32(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }

      var pos = 8, ihdr = null, plte = null, trns = null, head, len, type;
      for (;;) {
        head = await bytes(pos, 8);
        if (head.length < 8) throw 'eof';
        len = u32(head, 0);
        type = String.fromCharCode(head[4], head[5], head[6], head[7]);
        if (type === 'IDAT') break;
        if (type === 'IEND') throw 'eof';
        if (type === 'IHDR' || type === 'PLTE' || type === 'tRNS') {
          var body = await bytes(pos + 8, len);
          if (type === 'IHDR') ihdr = { w: u32(body, 0), h: u32(body, 4), depth: body[8], color: body[9], interlace: body[12] };
          else if (type === 'PLTE') plte = body;
          else trns = body;
        }
        pos += 12 + len;
      }
      if (!ihdr || ihdr.interlace) throw 'unsupported-png';

      var W = ihdr.w, depth = ihdr.depth, color = ihdr.color;
      var channels = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 }[color];
      var sample = channels && makeSampler(color, depth, plte, trns);
      if (!sample) throw 'unsupported-png';
      var bitsPerPixel = channels * depth;
      var stride = Math.ceil(W * bitsPerPixel / 8);
      var bpp = Math.max(1, bitsPerPixel >> 3);
      var sq = topSquare(W, ihdr.h, d.side), q = sq.q, T = sq.t;

      // sample a sparse grid of the square (unfiltering still touches every byte)
      var step = Math.max(1, Math.floor(q / T / 3));
      var cols = [], colOut = [];
      for (var cx = 0; cx < q; cx += step) {
        cols.push(sq.sx + cx);
        colOut.push(Math.min(T - 1, Math.floor(cx * T / q)));
      }
      var acc = new Float64Array(T * 4), cnt = new Uint32Array(T);
      var out = new Uint8ClampedArray(T * T * 4), px = new Uint8Array(4);
      var prev = new Uint8Array(stride), cur = new Uint8Array(stride);
      var filled = -1, ftype = 0, y = 0, band = 0;

      function flush(oy) {
        for (var i = 0; i < T; i++) {
          var n = cnt[i];
          if (!n) continue;
          var a = acc[i * 4 + 3], o = (oy * T + i) * 4;
          if (a > 0) {                              // alpha-weighted colour keeps cut-out edges clean
            out[o] = acc[i * 4] / a;
            out[o + 1] = acc[i * 4 + 1] / a;
            out[o + 2] = acc[i * 4 + 2] / a;
          }
          out[o + 3] = a / n;
        }
        acc.fill(0);
        cnt.fill(0);
      }

      function rowDone() {
        unfilter(ftype, cur, prev, bpp, stride);
        if (y % step === 0) {
          var oy = Math.min(T - 1, Math.floor(y * T / q));
          if (oy !== band) { flush(band); band = oy; }
          for (var k = 0; k < cols.length; k++) {
            sample(cur, cols[k], px);
            var j = colOut[k] * 4, a = px[3];
            acc[j] += px[0] * a;
            acc[j + 1] += px[1] * a;
            acc[j + 2] += px[2] * a;
            acc[j + 3] += a;
            cnt[colOut[k]]++;
          }
        }
        var swap = prev; prev = cur; cur = swap;
        y++;
      }

      var ds = new DecompressionStream('deflate');
      var writer = ds.writable.getWriter();
      var reader = ds.readable.getReader();
      var stop = false;

      // producer: feed consecutive IDAT payloads in slices of at most 1 MB
      (async function () {
        var p = pos;
        try {
          while (!stop) {
            var h8 = await bytes(p, 8);
            if (h8.length < 8 || String.fromCharCode(h8[4], h8[5], h8[6], h8[7]) !== 'IDAT') break;
            var L = u32(h8, 0);
            for (var off = 0; off < L && !stop; off += 1048576) {
              await writer.write(await bytes(p + 8 + off, Math.min(1048576, L - off)));
            }
            p += 12 + L;
          }
          if (!stop) await writer.close();
        } catch (err) { /* aborted once enough rows were read */ }
      })();

      try {
        while (y < q) {
          var r = await reader.read();
          if (r.done) break;
          var chunk = r.value, i = 0, n = chunk.length;
          while (i < n && y < q) {
            if (filled < 0) { ftype = chunk[i++]; filled = 0; continue; }
            var take = Math.min(stride - filled, n - i);
            cur.set(chunk.subarray(i, i + take), filled);
            filled += take;
            i += take;
            if (filled === stride) { rowDone(); filled = -1; }
          }
        }
      } finally {
        stop = true;
        reader.cancel().catch(function () {});
        writer.abort().catch(function () {});
      }
      if (!y) throw 'empty';
      flush(band);

      var canvas = new OffscreenCanvas(T, T);
      canvas.getContext('2d').putImageData(new ImageData(out, T, T), 0, 0);
      return { blob: await encode(canvas), w: T, h: T };
    }

    function unfilter(t, cur, prev, bpp, n) {
      var i, a, b, c, p, pa, pb, pc;
      if (t === 1) {
        for (i = bpp; i < n; i++) cur[i] = cur[i] + cur[i - bpp];
      } else if (t === 2) {
        for (i = 0; i < n; i++) cur[i] = cur[i] + prev[i];
      } else if (t === 3) {
        for (i = 0; i < bpp; i++) cur[i] = cur[i] + (prev[i] >> 1);
        for (; i < n; i++) cur[i] = cur[i] + ((cur[i - bpp] + prev[i]) >> 1);
      } else if (t === 4) {
        for (i = 0; i < bpp; i++) cur[i] = cur[i] + prev[i];
        for (; i < n; i++) {
          a = cur[i - bpp]; b = prev[i]; c = prev[i - bpp];
          p = a + b - c;
          pa = p > a ? p - a : a - p;
          pb = p > b ? p - b : b - p;
          pc = p > c ? p - c : c - p;
          cur[i] = cur[i] + (pa <= pb && pa <= pc ? a : pb <= pc ? b : c);
        }
      }
    }

    // writes the RGBA of pixel x of an unfiltered scanline into o
    function makeSampler(color, depth, plte, trns) {
      function pal(v, o) {
        var i = v * 3;
        o[0] = plte ? plte[i] : 0;
        o[1] = plte ? plte[i + 1] : 0;
        o[2] = plte ? plte[i + 2] : 0;
        o[3] = trns && v < trns.length ? trns[v] : 255;
      }
      if (depth === 8) {
        if (color === 6) return function (r, x, o) { var i = x * 4; o[0] = r[i]; o[1] = r[i + 1]; o[2] = r[i + 2]; o[3] = r[i + 3]; };
        if (color === 2) return function (r, x, o) { var i = x * 3; o[0] = r[i]; o[1] = r[i + 1]; o[2] = r[i + 2]; o[3] = 255; };
        if (color === 4) return function (r, x, o) { var i = x * 2; o[0] = o[1] = o[2] = r[i]; o[3] = r[i + 1]; };
        if (color === 0) return function (r, x, o) { o[0] = o[1] = o[2] = r[x]; o[3] = 255; };
        if (color === 3) return function (r, x, o) { pal(r[x], o); };
      }
      if (depth === 16) {
        if (color === 6) return function (r, x, o) { var i = x * 8; o[0] = r[i]; o[1] = r[i + 2]; o[2] = r[i + 4]; o[3] = r[i + 6]; };
        if (color === 2) return function (r, x, o) { var i = x * 6; o[0] = r[i]; o[1] = r[i + 2]; o[2] = r[i + 4]; o[3] = 255; };
        if (color === 4) return function (r, x, o) { var i = x * 4; o[0] = o[1] = o[2] = r[i]; o[3] = r[i + 2]; };
        if (color === 0) return function (r, x, o) { o[0] = o[1] = o[2] = r[x * 2]; o[3] = 255; };
      }
      if ((color === 0 || color === 3) && depth < 8) {
        var per = 8 / depth, mask = (1 << depth) - 1, scale = 255 / mask;
        return function (r, x, o) {
          var v = (r[(x / per) | 0] >> ((per - 1 - x % per) * depth)) & mask;
          if (color === 3) return pal(v, o);
          o[0] = o[1] = o[2] = v * scale;
          o[3] = 255;
        };
      }
      return null;
    }

    self.onmessage = async function (e) {
      var d = e.data;
      try {
        if (typeof OffscreenCanvas === 'undefined') throw 'unsupported';
        var res = d.mode === 'stream' ? await viaPngStream(d) : await viaBitmap(d);
        self.postMessage({ id: d.id, blob: res.blob, w: res.w, h: res.h });
      } catch (err) {
        self.postMessage({ id: d.id, error: String((err && err.message) || err) });
      }
    };
  }

  function mainThumb(job) {
    var px = job.w * job.h;
    if ((px && px > TH.mainMaxPixels) || (!px && job.file.size > 8e6)) return Promise.reject('too-big');
    var url = URL.createObjectURL(job.file);
    var img = new Image();
    img.decoding = 'async';
    img.src = url;
    return img.decode().then(function () {
      var sq = topSquare(img.naturalWidth, img.naturalHeight, TH.side);
      var canvas = document.createElement('canvas');
      canvas.width = canvas.height = sq.t;
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sq.sx, 0, sq.q, sq.q, 0, 0, sq.t, sq.t);
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (blob) resolve({ blob: blob, w: sq.t, h: sq.t });
          else reject('encode');
        }, 'image/webp', 0.82);
      });
    }).finally(function () { URL.revokeObjectURL(url); });
  }

  function paintById(id) {
    var item = document.getElementById('filepond--item-' + id);
    if (item) paintThumb(item, thumbs[id] && thumbs[id].ext);
  }

  // 1:1 card at the head of the row; idempotent, safe to call on every scan
  function paintThumb(item, ext) {
    var info = item.querySelector('.filepond--file-info');
    if (!info) return;
    var card = info.querySelector(':scope > .ucx-thumb');
    if (!card) {
      card = h('span', 'ucx-thumb');
      card.setAttribute('aria-hidden', 'true');
      info.insertBefore(card, info.firstChild);
      info.classList.add('ucx-info');
    }
    var t = thumbs[item.id.replace('filepond--item-', '')];
    card.setAttribute('data-ext', (t && t.ext) || ext || '');
    if (!t) return card.setAttribute('data-state', 'none');
    if (t.state !== 'ready') return card.setAttribute('data-state', t.state);

    var img = card.querySelector('img');
    if (img && img.getAttribute('src') === t.url) return;
    img = img || card.appendChild(document.createElement('img'));
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    if (t.ratio > 1) {                        // wide vector: full height, centred
      img.style.width = (t.ratio * 100) + '%';
      img.style.left = ((1 - t.ratio) * 50) + '%';
    } else if (t.ratio < 1) {                 // tall vector: full width, top
      img.style.height = (100 / t.ratio) + '%';
    }
    card.setAttribute('data-state', 'loading');
    img.src = t.url;
    (img.decode ? img.decode() : Promise.resolve()).then(function () {
      card.setAttribute('data-state', 'ready');
    }, function () {
      card.setAttribute('data-state', 'skip');
    });
  }

  /* -------------------------------------------------------------------------
     Success: spectrum burst + add-to-cart nudge once the last upload is done
     ---------------------------------------------------------------------- */
  function celebrateSoon(st, file) {
    if (file && file.id) st.lastId = file.id;
    clearTimeout(st.celebrate);
    st.celebrate = setTimeout(function () {
      // item states are space-separated lists ("busy processing")
      var busy = Array.prototype.some.call(st.root.querySelectorAll('.filepond--item'), function (item) {
        return /(^|\s)(busy|processing|processing-queued)(\s|$)/.test(item.getAttribute('data-filepond-item-state') || '');
      });
      if (busy) return;
      var item = st.lastId && document.getElementById('filepond--item-' + st.lastId);
      // burst from the finished row's status dot
      var pill = item && item.querySelector('.filepond--file-status-main');
      var p = pill && pill.offsetWidth ? pill.getBoundingClientRect() : null;
      var r = (item && item.offsetHeight ? item : st.root).getBoundingClientRect();
      if (p) spectrumBurst(p.left + 12, p.top + p.height / 2);
      else spectrumBurst(r.right - Math.min(r.width * 0.12, 48), r.top + Math.min(r.height / 2, 32));
      nudge(st.wrap);
    }, 320);
  }

  function spectrumBurst(x, y) {
    if (!CFG.celebrate || reduceMotion.matches) return;
    var fx = h('div', 'ucx-burst-fx');
    fx.setAttribute('aria-hidden', 'true');
    fx.style.left = x + 'px';
    fx.style.top = y + 'px';
    for (var k = 0; k < 12; k++) {
      var ray = document.createElement('i');
      ray.style.setProperty('--a', (k * 30 + 15) + 'deg');
      ray.style.setProperty('--c', 'var(' + SPECTRUM[k % SPECTRUM.length] + ')');
      ray.style.animationDelay = (k % 2) * 40 + 'ms';
      fx.appendChild(ray);
    }
    fx.appendChild(document.createElement('b'));
    document.body.appendChild(fx);
    setTimeout(function () { fx.remove(); }, 1000);
  }

  function nudge(wrap) {
    if (!CFG.nudgeAddToCart) return;
    var scopes = [wrap.closest('product-info'), wrap.closest('[id^="ProductInfo-"]'), wrap.closest('.product-info'), wrap.closest('section'), document];
    var btn = null;
    for (var i = 0; i < scopes.length && !btn; i++) {
      if (scopes[i]) btn = scopes[i].querySelector(
        'button[name="add"], form[action*="/cart/add"] [type="submit"]:not([name="plus"]):not([name="minus"])');
    }
    if (btn) replay(btn, 'ucx-nudge', 2400);
  }

  /* -------------------------------------------------------------------------
     Viewport: entrance once, pause loops while off-screen
     ---------------------------------------------------------------------- */
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var wrap = en.target.closest(WRAP);
      if (!wrap) return;
      wrap.classList.toggle('ucx-visible', en.isIntersecting);
      if (en.isIntersecting && en.intersectionRatio >= 0.3) wrap.classList.add('ucx-in');
    });
  }, { threshold: [0, 0.3] }) : null;

  /* -------------------------------------------------------------------------
     Page-wide file drag: arm every field while a file is being dragged
     ---------------------------------------------------------------------- */
  var depth = 0;
  function hasFiles(e) {
    var types = e.dataTransfer && e.dataTransfer.types;
    if (!types) return false;
    for (var i = 0; i < types.length; i++) if (types[i] === 'Files') return true;
    return false;
  }
  function disarm() {
    depth = 0;
    html.classList.remove('ucx-dragging');
  }
  window.addEventListener('dragenter', function (e) {
    if (!hasFiles(e)) return;
    depth++;
    html.classList.add('ucx-dragging');
  }, true);
  window.addEventListener('dragleave', function (e) {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (!depth) disarm();
  }, true);
  window.addEventListener('drop', disarm, true);
  window.addEventListener('dragend', disarm, true);
  window.addEventListener('pointermove', function () { if (depth) disarm(); }, { passive: true });

  /* -------------------------------------------------------------------------
     Discovery: the app builds the field asynchronously (and Eurus quick view
     / the theme editor can re-render it), so keep watching the DOM.
     ---------------------------------------------------------------------- */
  function scan() {
    var wraps = document.querySelectorAll(WRAP);
    if (!wraps.length) return;
    loadCss().then(function (ok) {
      if (!ok) return;
      wraps.forEach(function (wrap) {
        var st = wrap.__ucx;
        var root = wrap.querySelector(ROOT);
        if (st && root !== st.root) { teardown(st); st = null; }
        if (!st) return enhance(wrap);
        enhanceLabel(st);
        syncFiles(st);
      });
    });
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }

  new MutationObserver(schedule).observe(html, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();

  window.UCX = { version: '2.4.0', refresh: scan, burst: spectrumBurst };
})();
